// @flow

/* eslint react/no-did-mount-set-state:0 */
/* eslint react/forbid-prop-types:0 */

import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import ClassicGrid from 'app/common/react/components/growth/unauth/ClassicGrid/ClassicGrid';
import HolidayCard from 'app/common/react/components/growth/unauth/open/HolidayCard';
import InlineStyle from 'app/common/react/components/InlineStyle';
import Pin from 'app/common/react/components/growth/unauth/pin/Pin';
import PinGridAltCSS from 'app/common/react/components/growth/unauth/PinGrid/PinGridAltCSS';
import pinGridInnerCSS from 'app/common/react/components/growth/unauth/PinGrid/pinGridInnerCSS';
import PinWithImpressions from 'app/common/react/components/growth/unauth/pin/PinWithImpressions';
import PropTypes from 'prop-types';
import SimilarPinCardCSS from 'app/common/react/components/growth/unauth/PinLandingCloseup/SimilarPinCardCSS';
import UnauthGuidePageUpsellInBoardPage from 'app/common/react/components/growth/unauth/open/UnauthOpenBoardPage/UnauthGuidePageUpsellInBoardPage';
import UnauthPinnerHero from 'app/common/react/components/growth/unauth/open/search/PinnerHero/UnauthPinnerHero';
import { logCustomTraceEvent } from 'app/common/lib/TraceLogger/logTraceEvent';
import { type GridPin } from 'app/packages/pinterest-flow-types/pin';
import type { PinType, SrcSetType } from 'app/common/react/components/growth/unauth/lib/PropTypes';
import withUnauthSeoExperiments, {
  type UnauthSeoExperiments,
} from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  type PinImageClickHandlerType,
  type PinLinkClickHandlerType,
} from 'app/www-unauth/components/UnauthPageWrapper/UnauthPageWrapperContext';
import { Box, Masonry } from 'gestalt';
import { GRID_GUTTER_WIDTH, MIN_COLUMNS, getMaxColumns } from 'app/common/lib/react/gridUtils';

type Props = {|
  carouselTagColor: string,
  desktopUnauthRankingLoggingData: {
    id: string,
  },
  disableDeeplinks: boolean,
  enableOrderedList?: boolean,
  handlePinInGridClick: () => void,
  hideMeta?: boolean,
  hideAllMeta?: boolean,
  hideTitle?: boolean,
  hideVaseTags?: boolean,
  isPaidTraffic?: boolean,
  isRelatedPin: boolean,
  items: PinType[],
  loadPins: () => void,
  maxColumns: number,
  maxPinImageHeight: number,
  openInCurrentTab?: boolean,
  pinWrapperBackgroundColor: string,
  pinImageClickHandler: PinImageClickHandlerType,
  pinImageSize: string,
  pinImageSizeOverrides: string[],
  pinImageWidthMax: number,
  pinImageWidthMin: number,
  pinLinkClickHandler: PinLinkClickHandlerType,
  responsive?: boolean,
  scrollContainer: () => HTMLElement,
  secondaryFeed: boolean,
  showSeeMore: boolean,
  showSignupModal: ({ event: SyntheticMouseEvent<> }) => void,
  showRelatedPinsHeader?: boolean,
  showRichPinAttribution?: boolean,
  srcSet: SrcSetType,
  vaseCarouselStyleOverrides?: Object,
  viewParameter: number,
  viewType: number,
  viewData: {
    interest: string,
  },
  numServerRenderedPins: number,
  seoUnauthExperiments: UnauthSeoExperiments,
|};

type State = {|
  clientJsActive: boolean,
  expandedVASE: Object,
  fullImage: Object,
  secondaryFeed: boolean,
|};

type GridType = {|
  scrollContainer: any,
  virtualize?: boolean,
|};

const cssStyles = (pins, context, seoUnauthExperiments) =>
  pinGridInnerCSS() + SimilarPinCardCSS() + PinGridAltCSS(pins, context, seoUnauthExperiments);

export class PinGridInner extends Component<Props, State> {
  static defaultProps = {
    pinImageSize: '236x',
    numServerRenderedPins: 0,
  };

  state = {
    clientJsActive: false,
    expandedVASE: {},
    fullImage: {},
    secondaryFeed: this.props.secondaryFeed,
  };

  componentDidMount() {
    this.setState({ clientJsActive: true }, this.markResourcesFetchingTime);
  }

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (
      nextProps &&
      nextProps.items &&
      nextProps.items.length > 0 &&
      nextProps.items[0].type === 'placeholder' &&
      this.props.items[0].type === 'placeholder' &&
      nextProps.items[0].height !== this.props.items[0].height &&
      this.gridRef
    ) {
      this.gridRef.reflow();
    }
  }

  getPinImageSize = (item: PinType, idx: number) => {
    if (this.props.pinImageSizeOverrides && this.props.pinImageSizeOverrides[idx]) {
      return this.props.pinImageSizeOverrides[idx];
    } else {
      return this.props.pinImageSize in item.images ? this.props.pinImageSize : '236x';
    }
  };

  gridRef: ?(ClassicGrid | Masonry<GridPin>);

  calculateContainerStyle = (
    maxColumns: number,
    minColumns: number,
    pinImageWidthMax: number,
    gutterWidth: number,
  ): Object => {
    const containerStyle = {};
    if (Number.isInteger(maxColumns) && maxColumns > minColumns) {
      const maxWidth = maxColumns * (pinImageWidthMax + gutterWidth);
      containerStyle.margin = 'auto';
      containerStyle.maxWidth = maxWidth;
      containerStyle.overflow = 'hidden';
    }
    return containerStyle;
  };

  vaseClickHandler = (pin: PinType): void => {
    const expandedVASE = {
      ...this.state.expandedVASE,
      [pin.id]: !this.state.expandedVASE[pin.id],
    };

    const setStateCallback = () => this.gridRef && this.gridRef.reflow();
    this.setState({ expandedVASE }, setStateCallback);
  };

  markResourcesFetchingTime = (): void => {
    const imageSignatureResources = [];
    this.props.items.forEach((item, index) => {
      if (item.type === 'pin' && item.images) {
        imageSignatureResources.push(item.images[this.getPinImageSize(item, index)].url);
      }
    });

    if (imageSignatureResources.length > 0) {
      logCustomTraceEvent({
        name: 'PageLoadRegisterGridImagesTraceEvent',
        viewportFilled: true,
        gridData: this.props.items,
        el: ReactDOM.findDOMNode(this),
      });
      // Send grid mounted to v2 PWT traceLogger
      logCustomTraceEvent({ name: 'MasonryDidMount' });
    }
  };

  mergeItems = () =>
    // $FlowFixMe: new errors found from flow 0.96 upgrade
    this.props.items.map(
      (item) =>
        !this.state.expandedVASE[item.id] && !this.state.fullImage[item.id]
          ? item
          : {
              ...item,
              expandedVASE: this.state.expandedVASE[item.id],
              fullImage: this.state.fullImage[item.id],
            },
    );

  renderGridItem = (props: Object) => {
    const item = props.data;
    const inSemanticGroup =
      (this.context.pageType === 'pin' &&
        (
          this.props.seoUnauthExperiments.v2GetGroup('pin_grid_style_format_optimization_pin_v3') ||
          ''
        ).startsWith('enabled')) ||
      (this.context.pageType === 'board' &&
        (
          this.props.seoUnauthExperiments.v2GetGroup(
            'pin_grid_style_format_optimization_board_v3',
          ) || ''
        ).startsWith('enabled'));

    switch (item.type) {
      case 'pin':
        const pinProps = {
          clientJsActive: this.state.clientJsActive,
          carouselTagColor: this.props.carouselTagColor,
          desktopUnauthRankingLoggingData: this.props.desktopUnauthRankingLoggingData,
          disableDeeplinks: this.props.disableDeeplinks,
          maxPinImageHeight: this.props.maxPinImageHeight,
          handlePinInGridClick: this.props.handlePinInGridClick,
          isPaidTraffic: this.props.isPaidTraffic,
          pin: item,
          pinImageClickHandler: this.props.pinImageClickHandler,
          pinImageSize: this.getPinImageSize(item, props.itemIdx),
          pinImageWidthMax: this.props.pinImageWidthMax,
          pinImageWidthMin: this.props.pinImageWidthMin,
          pinLinkClickHandler: this.props.pinLinkClickHandler,
          pinNumber: props.itemIdx,
          hideMeta: this.props.hideMeta,
          hideAllMeta: this.props.hideAllMeta,
          hideTitle: this.props.hideTitle,
          hideVaseTags: this.props.hideVaseTags,
          showSeeMore: this.props.showSeeMore,
          srcSet: this.props.srcSet,
          openInCurrentTab: this.props.openInCurrentTab,
          vaseCarouselStyleOverrides: this.props.vaseCarouselStyleOverrides,
          vaseClickHandler: this.vaseClickHandler,
          secondaryFeed: this.state.secondaryFeed,
          showRichPinAttribution: this.props.showRichPinAttribution,
          isMainPin: props.itemIdx < 5 && !this.props.secondaryFeed,
          fromRelatedPinSection: true,
        };

        const htmlInner = (
          <PinWithImpressions
            impressionType={'Pin'}
            isRelatedPin={this.props.isRelatedPin}
            loggingId={item.id}
            slotIndex={props.itemIdx}
            viewParameter={
              this.props.viewParameter !== undefined
                ? this.props.viewParameter
                : (this.context.logging || {}).viewParameter
            }
            viewType={this.props.viewType || (this.context.logging || {}).viewType}
            viewData={this.props.viewData || (this.context.logging || {}).viewData}
            {...pinProps}
          />
        );

        return inSemanticGroup ? htmlInner : <Box>{htmlInner}</Box>;
      case 'placeholder':
        // Render placeholder pins to enable to create an empty space for other content (ie: an inline Pin Closeup)
        return (
          <div
            className="PinGrid__placeholder"
            style={{
              height: item.height + 'px',
              width: this.props.pinImageWidthMax + 'px',
            }}
          />
        );
      case 'module':
        if (item.name === 'PinnerHero') {
          return (
            <UnauthPinnerHero
              description={item.options.description}
              followerCount={item.options.follower_count}
              showFollowButton={item.options.show_follow_button}
              showSignupModal={this.props.showSignupModal}
              user={item.data}
              verified={item.options.verified}
            />
          );
        } else {
          return <div />;
        }

      case 'holidayCard':
        const htmlHalloweenCard = (
          /* $FlowFixMe: new errors found from flow 0.96 upgrade */
          <HolidayCard
            anchorTitle={item.anchorTitle}
            buttonText={item.buttonText}
            image={item.image}
            imageWidthMax={this.props.pinImageWidthMax}
            imageDescription={item.description}
            link={item.link}
            title={item.title}
          />
        );

        return inSemanticGroup ? htmlHalloweenCard : <Box>{htmlHalloweenCard}</Box>;

      case 'guidePageUpsell':
        return <UnauthGuidePageUpsellInBoardPage />;

      default:
        return <div />;
    }
  };

  renderServerRenderedGridItem(item: PinType, idx: number) {
    if (item.type !== 'pin') {
      return null;
    }

    return (
      <Box key={idx}>
        <Pin
          carouselTagColor={this.props.carouselTagColor}
          clientJsActive={this.state.clientJsActive}
          desktopUnauthRankingLoggingData={this.props.desktopUnauthRankingLoggingData}
          disableDeeplinks={this.props.disableDeeplinks}
          key={idx}
          maxPinImageHeight={this.props.maxPinImageHeight}
          pin={item}
          pinImageClickHandler={this.props.pinImageClickHandler}
          pinImageSize={this.getPinImageSize(item, idx)}
          pinImageWidthMax={this.props.pinImageWidthMax}
          pinImageWidthMin={this.props.pinImageWidthMin}
          pinWrapperBackgroundColor={this.props.pinWrapperBackgroundColor}
          pinNumber={idx}
          hideMeta={this.props.hideMeta}
          hideAllMeta={this.props.hideAllMeta}
          hideTitle={this.props.hideTitle}
          hideVaseTags={this.props.hideVaseTags}
          showSeeMore={this.props.showSeeMore}
          srcSet={this.props.srcSet}
          openInCurrentTab={this.props.openInCurrentTab}
          numServerRenderedPins={this.props.numServerRenderedPins}
          vaseClickHandler={this.vaseClickHandler}
          vaseCarouselStyleOverrides={this.props.vaseCarouselStyleOverrides}
          viewParameter={
            this.props.viewParameter !== undefined
              ? this.props.viewParameter
              : (this.context.logging || {}).viewParameter
          }
          isPaidTraffic={this.props.isPaidTraffic}
          showRichPinAttribution={this.props.showRichPinAttribution}
          isMainPin={idx < 5 && !this.props.secondaryFeed}
        />
      </Box>
    );
  }

  render() {
    const items = this.mergeItems();
    const minColumns = MIN_COLUMNS;
    const gutterWidth = GRID_GUTTER_WIDTH;
    const inPinGridOptGroup =
      (this.context.pageType === 'pin' &&
        (
          this.props.seoUnauthExperiments.v2GetGroup('pin_grid_style_format_optimization_pin_v3') ||
          ''
        ).startsWith('enabled')) ||
      (this.context.pageType === 'board' &&
        (
          this.props.seoUnauthExperiments.v2GetGroup(
            'pin_grid_style_format_optimization_board_v3',
          ) || ''
        ).startsWith('enabled'));

    const maxColumnContainerStyle = this.calculateContainerStyle(
      this.props.maxColumns,
      minColumns,
      this.props.pinImageWidthMax,
      gutterWidth,
    );

    const marginTop = this.props.showRelatedPinsHeader ? '16px' : '-20px';
    const gridContainerStyle = { ...maxColumnContainerStyle, marginTop };
    if (this.props.responsive) {
      gridContainerStyle.width = '100%';
    }

    const pinGridSSRNumber = getMaxColumns(items.length);
    const renderItem = items.slice(0, pinGridSSRNumber);

    if (!this.state.clientJsActive && this.props.numServerRenderedPins > 0) {
      // Only executes if we are rendering grid of pins on server
      return inPinGridOptGroup ? (
        <section
          className="gridCentered pinGrid"
          data-test-id="pinGrid"
          style={{ ...gridContainerStyle, position: 'relative' }}
        >
          {renderItem.map((item, idx) => this.renderServerRenderedGridItem(item, idx))}
        </section>
      ) : (
        <div
          className="gridCentered pinGrid"
          data-test-id="pinGrid"
          style={{ ...gridContainerStyle, position: 'relative' }}
        >
          {renderItem.map((item, idx) => this.renderServerRenderedGridItem(item, idx))}
        </div>
      );
    } else {
      const gridProps: GridType = {
        scrollContainer: this.props.scrollContainer || undefined,
      };

      const innerHtml = (
        <React.Fragment>
          <InlineStyle
            unsafeCSS={cssStyles(items, this.context, this.props.seoUnauthExperiments)}
          />
          <ClassicGrid
            columnWidth={this.props.pinImageWidthMax}
            comp={this.renderGridItem}
            enableOrderedList={this.props.enableOrderedList}
            gutterWidth={gutterWidth}
            items={items}
            loadItems={this.props.loadPins}
            minCols={this.props.responsive ? 1 : minColumns}
            ref={(ref) => {
              this.gridRef = ref;
            }}
            {...gridProps}
          />
        </React.Fragment>
      );
      return inPinGridOptGroup ? (
        <section className="gridCentered" data-test-id="pinGrid" style={{ marginTop }}>
          {innerHtml}
        </section>
      ) : (
        <div className="gridCentered" data-test-id="pinGrid" style={gridContainerStyle}>
          {innerHtml}
        </div>
      );
    }
  }
}

PinGridInner.contextTypes = {
  currentUrl: PropTypes.string,
  isBot: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  logging: PropTypes.shape({
    viewParameter: PropTypes.number,
    viewType: PropTypes.number.isRequired,
    viewData: PropTypes.shape({
      interest: PropTypes.string,
    }),
  }),
  pageType: PropTypes.string.isRequired,
};

export default withUnauthSeoExperiments(PinGridInner);
